import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import type { Test } from "@/types";
import { Card } from "@/components/ui/Card";
import { Input } from "@/components/ui/Input";
import { Button } from "@/components/ui/Button";
import { Spinner } from "@/components/ui/Spinner";

export default function TestEditor() {
  const { id = "" } = useParams<{ id: string }>();
  const [test, setTest] = useState<Test | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  useEffect(() => {
    api.get<Test>(`/admin/tests/${id}`)
      .then(setTest)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [id]);

  async function save(ev: React.FormEvent) {
    ev.preventDefault();
    if (!test) return;
    setSaving(true); setError(null);
    try {
      const updated = await api.put<Test>(`/admin/tests/${id}`, {
        titolo: test.titolo,
        slug: test.slug,
        descrizione: test.descrizione ?? null,
        durataMinuti: test.durataMinuti ?? null,
        questioniPerStudente: test.questioniPerStudente ?? null,
        pubblicoAnonimo: test.pubblicoAnonimo,
      });
      setTest(updated);
      setSavedAt(Date.now());
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Spinner size={32} /></div>;
  if (!test) return <div className="p-10 text-center text-red-300">{error ?? "Non trovato"}</div>;

  return (
    <div className="max-w-2xl mx-auto px-4 py-8 space-y-5">
      <header className="flex justify-between items-baseline">
        <h1 className="text-2xl font-serif text-primary">{test.titolo}</h1>
        <Link to="/admin/tests" className="font-mono text-xs text-secondary hover:text-primary">← lista</Link>
      </header>

      <Card>
        <form onSubmit={save} className="space-y-4">
          <Input label="Titolo" value={test.titolo} onChange={(e) => setTest({ ...test, titolo: e.target.value })} required />
          <Input
            label="Slug"
            value={test.slug}
            onChange={(e) => setTest({ ...test, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-") })}
            required
          />
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-mono uppercase tracking-wider text-secondary">Descrizione</label>
            <textarea
              value={test.descrizione ?? ""}
              onChange={(e) => setTest({ ...test, descrizione: e.target.value })}
              rows={3}
              className="bg-bg border border-border rounded-lg px-4 py-3 text-primary focus:outline-none focus:border-accent"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Input
              label="Durata (min)"
              type="number"
              value={test.durataMinuti ?? ""}
              onChange={(e) => setTest({ ...test, durataMinuti: e.target.value ? Number(e.target.value) : undefined })}
            />
            <Input
              label="Domande per studente"
              type="number"
              value={test.questioniPerStudente ?? ""}
              onChange={(e) => setTest({ ...test, questioniPerStudente: e.target.value ? Number(e.target.value) : undefined })}
            />
          </div>
          <label className="flex items-center gap-2 text-sm text-secondary">
            <input
              type="checkbox"
              checked={test.pubblicoAnonimo}
              onChange={(e) => setTest({ ...test, pubblicoAnonimo: e.target.checked })}
            />
            Accessibile anonimamente (test di ingresso senza login)
          </label>

          {error && <div className="text-red-300 text-sm">{error}</div>}

          <div className="flex items-center gap-3">
            <Button type="submit" loading={saving}>Salva</Button>
            {savedAt && <span className="text-xs text-green font-mono">✓ salvato</span>}
          </div>
        </form>
      </Card>
    </div>
  );
}
